import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoPersonSharp, IoLocationSharp, IoCallSharp, IoMailSharp, IoBusinessSharp } from "react-icons/io5";
import { FaChevronDown, FaChevronUp, FaFileInvoiceDollar, FaHammer, FaGlobe } from "react-icons/fa";
import { MdOutlineArchitecture, MdKeyboardDoubleArrowLeft, MdAdd } from "react-icons/md";
import { SiTestrail } from "react-icons/si";
import { notify, Toast } from "../../common/Toast";
import axiosInstance from "../../api/axiosInstance";

const ViewDetails = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const siteData = state?.siteData;
  const [tab, setTab] = useState("overview");
  const [transaction, setTransaction] = useState({
    windowQuotation: [],
    manualQuotation: [],
  });
  const [showWindowQuotation, setShowWindowQuotation] = useState(true);
  const [showManualQuotation, setShowManualQuotation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOwner, setShowOwner] = useState(true);
  const [showArchitect, setShowArchitect] = useState(false);
  const [showFabricator, setShowFabricator] = useState(false);

  const fetchTransaction = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(
        `SiteDetail/GetBySite/${siteData.site_Id}`
      );
      setTransaction(data || { windowQuotation: [], manualQuotation: [] });
    } catch (err) {
      notify("Error fetching transaction data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      tab === "transaction" &&
      transaction.windowQuotation?.length === 0 &&
      transaction.manualQuotation?.length === 0
    ) {
      fetchTransaction();
    }
  }, [tab]);

  if (!siteData) return <div className="p-10 text-center font-bold text-slate-400">Loading site data...</div>;

  return (
    <div className="max-w-[1600px] mx-auto pb-12">
      <Toast />

      {/* Top Navigation */}
      <div className="flex items-center justify-between mb-8 px-2">
        <button
          className="group flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 font-bold text-sm rounded-xl hover:bg-slate-50 transition-all shadow-sm"
          onClick={() => navigate("/transaction/sites")}
        >
          <MdKeyboardDoubleArrowLeft className="text-lg group-hover:-translate-x-1 transition-transform" />
          Back to Sites
        </button>
        <div className="flex items-center gap-3">
          <button
            className="px-5 py-2 bg-primary/10 text-primary font-bold text-sm rounded-xl hover:bg-primary/20 transition-all"
            onClick={() => navigate("/transaction/site-details", { state: { siteData } })}
          >
            Edit Site
          </button>
        </div>
      </div>

      {/* Main Profile Header */}
      <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden mb-8">
        <div className="relative h-32 bg-gradient-to-r from-primary/80 to-blue-600">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        </div>
        <div className="px-10 pb-10">
          <div className="relative flex flex-col md:flex-row items-end gap-6 -mt-12">
            <div className="w-28 h-28 bg-white rounded-3xl shadow-xl flex items-center justify-center border-4 border-white text-primary">
              <SiTestrail className="text-[44px]" />
            </div>
            <div className="flex-1 pb-2">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-3xl font-black text-slate-800 tracking-tight">{siteData.site_Name}</h1>
              </div>
              <p className="flex items-center gap-2 text-slate-500 font-bold text-sm">
                <IoLocationSharp className="text-primary" />
                {siteData.address}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 pb-2">
              <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-2xl border border-slate-100">
                <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-[12px] font-bold">
                  {siteData.sale_Person_Name?.charAt(0) || "S"}
                </div>
                <span className="font-bold text-slate-700 text-sm whitespace-nowrap">{siteData.sale_Person_Name || "Unassigned"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-8 bg-white/50 p-1.5 rounded-2xl border border-slate-100 w-fit">
        <button
          className={`px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${tab === "overview"
            ? "bg-primary text-white shadow-lg shadow-primary/25"
            : "text-slate-500 hover:bg-white hover:text-slate-700"
            }`}
          onClick={() => setTab("overview")}
        >
          General Overview
        </button>
        <button
          className={`px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${tab === "transaction"
            ? "bg-primary text-white shadow-lg shadow-primary/25"
            : "text-slate-500 hover:bg-white hover:text-slate-700"
            }`}
          onClick={() => setTab("transaction")}
        >
          Financial Records
        </button>
      </div>

      {/* Tab Content: Overview */}
      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Site Quick Stats / Info */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-6">
              <h3 className="text-[13px] font-black text-slate-800 uppercase tracking-[0.15em] border-b border-slate-50 pb-4">Property Specs</h3>

              <div className="space-y-5">
                <InfoRow icon={<IoBusinessSharp />} label="Property Type" value={siteData.site_Type} />
                <InfoRow icon={<IoCallSharp />} label="Contact Number" value={siteData.mobile_No} />
                <DetailBox label="Remark & Notes" value={siteData.Remark} />
                {siteData.location_Link && (
                  <a
                    href={siteData.location_Link}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-3 bg-slate-50 text-primary font-bold text-xs uppercase tracking-widest rounded-2xl border border-slate-100 hover:bg-primary hover:text-white transition-all"
                  >
                    <FaGlobe /> Open Map Location
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Stakeholders Sections */}
          <div className="lg:col-span-8 space-y-4">
            <StakeholderSection
              title="Property Owner"
              icon={<IoPersonSharp />}
              isOpen={showOwner}
              onToggle={() => setShowOwner(!showOwner)}
              accent="indigo"
              data={[
                { label: "Contact Name", value: siteData.owner_Name },
                { label: "Phone", value: siteData.owner_Contact_No },
                { label: "Email", value: siteData.owner_Email_Id },
                { label: "Firm/Company", value: siteData.owner_Firm_Name },
                { label: "Office Address", value: siteData.owner_Office_Address },
              ]}
            />

            <StakeholderSection
              title="Architect & Design"
              icon={<MdOutlineArchitecture />}
              isOpen={showArchitect}
              onToggle={() => setShowArchitect(!showArchitect)}
              accent="emerald"
              data={[
                { label: "Lead Architect", value: siteData.architect_Name },
                { label: "Office Phone", value: siteData.architect_Contact_No },
                { label: "Professional Email", value: siteData.architect_Email_Id },
                { label: "Architecture Firm", value: siteData.architect_Firm_Name },
                { label: "Studio Address", value: siteData.architect_Office_Address },
              ]}
            />

            <StakeholderSection
              title="Fabrication Partner"
              icon={<FaHammer />}
              isOpen={showFabricator}
              onToggle={() => setShowFabricator(!showFabricator)}
              accent="amber"
              data={[
                { label: "Partner Name", value: siteData.fabricator_Name },
                { label: "Support Line", value: siteData.fabricator_Contact_No },
                { label: "Inquiry Email", value: siteData.fabricator_Email_Id },
                { label: "Fabrication Facility", value: siteData.fabricator_Firm_Name },
                { label: "Facility Address", value: siteData.fabricator_Office_Address },
              ]}
            />
          </div>
        </div>
      )}

      {/* Tab Content: Transaction */}
      {tab === "transaction" && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
          {loading ? (
            <div className="py-20 flex justify-center"><div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div></div>
          ) : (
            <>
              {/* Window Quotations */}
              <TransactionGroup
                title="System Window Quotations"
                count={transaction.windowQuotation?.length || 0}
                isOpen={showWindowQuotation}
                onToggle={() => setShowWindowQuotation(!showWindowQuotation)}
                onCreate={() => navigate("/quotes/window-quotation/active-details", { state: { selectedSite: siteData } })}
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50/80 text-slate-500 uppercase tracking-wider text-[10px] font-black border-y border-slate-100">
                      <tr>
                        <th className="px-6 py-4">Quote Number</th>
                        <th className="px-6 py-4">Date Issued</th>
                        <th className="px-6 py-4">Status / Stage</th>
                        <th className="px-6 py-4">Total Sqft</th>
                        <th className="px-6 py-4 text-right">Quote Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {transaction.windowQuotation?.map((item, i) => (
                        <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-6 py-4 font-bold text-slate-700">{item.quotation_No}</td>
                          <td className="px-6 py-4 text-slate-500 font-medium">{item.quotation_Date.split("T")[0]}</td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-blue-50 text-primary text-[10px] font-bold rounded-full uppercase">
                              {item.stage_Name}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-bold text-slate-600">{item.total_Sqft} <span className="text-[10px] text-slate-400">FT²</span></td>
                          <td className="px-6 py-4 text-right font-black text-slate-800">₹{parseFloat(item.quote_Amount).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TransactionGroup>

              {/* Manual Quotations */}
              <TransactionGroup
                title="Manual & Custom Quotations"
                count={transaction.manualQuotation?.length || 0}
                isOpen={showManualQuotation}
                onToggle={() => setShowManualQuotation(!showManualQuotation)}
                onCreate={() => navigate("/quotes/manual-quotation/manual-details", { state: { selectedSite: siteData } })}
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50/80 text-slate-500 uppercase tracking-wider text-[10px] font-black border-y border-slate-100">
                      <tr>
                        <th className="px-6 py-4">Quote Number</th>
                        <th className="px-6 py-4">Created On</th>
                        <th className="px-6 py-4">Current Stage</th>
                        <th className="px-6 py-4">Client Company</th>
                        <th className="px-6 py-4 text-right">Net Valuation</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {transaction.manualQuotation?.map((item, i) => (
                        <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-6 py-4 font-bold text-slate-700">{item.quotation_No}</td>
                          <td className="px-6 py-4 text-slate-500 font-medium">{item.quotation_Date.split("T")[0]}</td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 bg-amber-50 text-amber-700 text-[10px] font-bold rounded-full uppercase">
                              {item.stage_Name}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-bold text-slate-600">{item.company_Name}</td>
                          <td className="px-6 py-4 text-right font-black text-slate-800">₹{parseFloat(item.net_Amount).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TransactionGroup>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// --- Sub Components ---

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-4">
    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-primary border border-slate-100 flex-shrink-0">
      <span className="text-lg">{icon}</span>
    </div>
    <div className="flex flex-col">
      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
      <span className="text-[14px] font-bold text-slate-700">{value || "—"}</span>
    </div>
  </div>
);

const DetailBox = ({ label, value }) => (
  <div className="space-y-2">
    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
      <p className="text-[13px] font-medium text-slate-600 italic leading-relaxed">
        {value || "No additional remarks were provided for this site location."}
      </p>
    </div>
  </div>
);

const StakeholderSection = ({ title, icon, isOpen, onToggle, data, accent }) => {
  const accentMap = {
    indigo: "from-indigo-500 to-indigo-600 bg-indigo-50 text-indigo-600",
    emerald: "from-emerald-500 to-emerald-600 bg-emerald-50 text-emerald-600",
    amber: "from-amber-500 to-amber-600 bg-amber-50 text-amber-600",
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden transition-all">
      <div
        className="px-8 py-5 flex items-center justify-between cursor-pointer hover:bg-slate-50/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${accentMap[accent].split(' ')[2]} ${accentMap[accent].split(' ')[1]}`}>
            {icon}
          </div>
          <h2 className="text-[14px] font-black text-slate-800 uppercase tracking-wider">{title}</h2>
        </div>
        <div className="flex items-center gap-3">
          {!isOpen && <span className="text-[11px] font-bold text-slate-400">{data[0].value}</span>}
          {isOpen ? <FaChevronUp className="text-slate-400" /> : <FaChevronDown className="text-slate-400" />}
        </div>
      </div>

      {isOpen && (
        <div className="px-8 pb-8 pt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in slide-in-from-top-2 duration-300">
          {data.map((item, idx) => (
            <div key={idx} className={`flex flex-col ${idx === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{item.label}</span>
              <span className="text-[13px] font-bold text-slate-700 break-all">{item.value || "—"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TransactionGroup = ({ title, count, isOpen, onToggle, onCreate, children }) => (
  <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
    <div className="px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="flex items-center gap-4 cursor-pointer" onClick={onToggle}>
        <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-primary group transition-all">
          <FaFileInvoiceDollar className="text-lg group-hover:scale-110 transition-transform" />
        </div>
        <div>
          <h3 className="text-[15px] font-black text-slate-800 uppercase tracking-tight">{title}</h3>
          <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest">{count} Records Found</p>
        </div>
        {isOpen ? <FaChevronUp className="text-slate-300 ml-2" /> : <FaChevronDown className="text-slate-300 ml-2" />}
      </div>

      <button
        onClick={onCreate}
        className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-[11px] font-black uppercase tracking-widest rounded-xl hover:shadow-lg shadow-primary/20 transition-all"
      >
        <MdAdd className="text-lg" /> New Quote
      </button>
    </div>

    {isOpen && (
      <div className="animate-in fade-in duration-500">
        {count > 0 ? children : (
          <div className="px-8 pb-10 pt-4 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-50 rounded-full mb-4">
              <FaFileInvoiceDollar className="text-slate-200 text-2xl" />
            </div>
            <p className="text-slate-400 text-sm font-bold uppercase tracking-widest">No transaction history available</p>
          </div>
        )}
      </div>
    )}
  </div>
);

export default ViewDetails;
